import { BestOfRingVideo } from '@amzn/ring-neighbors-contentful-gql-schema';
import { Box, Flex } from '@amzn/ring-ui-react-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import ReactPlayer from 'react-player';
import { useParams } from 'react-router-dom';
import {
  ItemType,
  useNeighborsWebClickEventStreamDispatcher,
} from 'src/components/App/hooks';
import { useAuth } from 'src/context';
import { useGetBestOfRingVideoById } from 'src/features/BestOfRing/hooks/useGetBestOfRingVideoById';
import { SubmitVideoModal } from 'src/features/BestOfRing/modals/SubmitVideoModal';
import { useHasWebViewUserAgent } from 'src/hooks';
import { logBugsnag, SEVERITY } from 'src/logs';
import { Loading } from 'src/shared/components/Loading';
import {
  useIsAndroidOS,
  useIsDesktop,
  useIsiOS,
  useIsMobile,
  useIsTablet,
  useStateRef,
} from 'src/shared/hooks';
import { useIsChrome } from 'src/shared/hooks/useIsChrome';
import { reLoginWebview } from 'src/utils';
import { RouterParams } from './../types';
import { VideoThumbnailItem } from './VideoThumbnailItem';
import { NotFound } from '../components/NotFound';
import { useBestOfRingContext } from '../context';
import { buildPageTitle } from '../utils';

type VideoStateProps = {
  played: number;
  playedSeconds: number;
  duration: number;
  loaded: number;
  playing: boolean;
  muted: boolean;
  ended: boolean;
  hasStarted: boolean;
};

const initialVideoState = {
  played: 0,
  playedSeconds: 0,
  duration: 0,
  loaded: 0,
  playing: true,
  muted: false,
  ended: false,
  hasStarted: false,
};

const updateVideoStateArray = (
  stateArray: VideoStateProps[],
  index: number,
  newVideoState: VideoStateProps,
) =>
  stateArray.map((state, i) => {
    if (i === index) {
      return newVideoState;
    } else {
      return state;
    }
  });

const sendTelemetry = (
  dispatchClick:
    | ((item: ItemType, tags?: string[], properties?: {}) => Promise<void>)
    | undefined,
  newVideo: BestOfRingVideo,
  currentVideo: BestOfRingVideo,
) => {
  if (dispatchClick) {
    dispatchClick('nhWeb_videoClick', [newVideo.name!], {
      itemType: 'button',
      referringItem: currentVideo.name,
    });
  }
};

export const FullscreenVideoPage = () => {
  const { id_video } = useParams<RouterParams>();
  const isChrome = useIsChrome();
  const { isPending, isUnauthenticated } = useAuth();
  const [itemSize, setItemSize] = useState(1);
  const isDesktop = useIsDesktop();
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const isIos = useIsiOS();
  const isAndroid = useIsAndroidOS();
  const isWebview = useHasWebViewUserAgent();
  const [isSubmitVideoModalOpen, setIsSubmitVideoModalOpen] =
    useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { videoList, loading: videoCollectionListLoading } =
    useBestOfRingContext();
  const [videos, setVideos] = useState<BestOfRingVideo[]>([]);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();
  const isScrollTimeoutRef = useRef<NodeJS.Timeout>();
  const scrollableRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef<boolean>();
  const isDesktopScrollCompleteRef = useRef<boolean>(true);
  const [isScrolling, setIsScrolling] = useState<boolean>(false);
  const touchStart = useRef<number | null>(null);
  const touchEnd = useRef<number | null>(null);
  const [currentVideoIndex, setCurrentVideoIndex, currentVideoIndexRef] =
    useStateRef<number>(0);
  const [hasTriedPlayingMuted, setHasTriedPlayingMuted] = useState(false);

  const { data: video, loading: loadingVideo } =
    useGetBestOfRingVideoById(id_video);

  const playerRef = useRef<ReactPlayer>(null);
  const [isBuffering, setIsBuffering] = useState<boolean>(false);
  const [videoState, setVideoState] = useState<VideoStateProps[]>([]);
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const bounceStartRef = useRef<HTMLDivElement>(null);
  const bounceEndRef = useRef<HTMLDivElement>(null);

  const dispatchClick =
    useNeighborsWebClickEventStreamDispatcher('nhWeb_bestOfRing');

  const dispatchVideoPageClick = useNeighborsWebClickEventStreamDispatcher(
    'nhWeb_bestOfRingVideo',
  );

  useEffect(() => {
    if (isWebview && isUnauthenticated && !isPending) {
      reLoginWebview();
    }
  }, [isUnauthenticated, isWebview, isPending]);

  useEffect(() => {
    const setSize = () => {
      const size = scrollableRef.current?.clientHeight || 0;
      setItemSize(size);
    };
    setSize();
    window.addEventListener('resize', setSize);

    return () => {
      window.removeEventListener('resize', setSize);
    };
  }, [scrollableRef, scrollableRef.current?.clientHeight]);

  useEffect(() => {
    setVideoState((s) =>
      updateVideoStateArray(s, currentVideoIndex, {
        ...s[currentVideoIndex],
        playing: true,
      }),
    );
  }, [currentVideoIndex]);

  const onPlay = () => {
    setIsBuffering(false);
  };

  const play = () => {
    setVideoState((s) =>
      updateVideoStateArray(s, currentVideoIndex, {
        ...s[currentVideoIndex],
        playing: true,
      }),
    );
  };

  const pause = () => {
    setVideoState((s) =>
      updateVideoStateArray(s, currentVideoIndex, {
        ...s[currentVideoIndex],
        playing: false,
      }),
    );
  };

  const toggleMute = (): void => {
    setVideoState((s) =>
      updateVideoStateArray(s, currentVideoIndex, {
        ...s[currentVideoIndex],
        muted: !s[currentVideoIndex].muted,
      }),
    );
  };

  const onBuffer = () => {
    setIsBuffering(true);
  };

  const onBufferEnd = () => {
    setIsBuffering(false);
  };

  const onEnded = () => {
    pause();
  };

  const onDuration = (duration: number) => {
    setVideoState((state) =>
      updateVideoStateArray(state, currentVideoIndex, {
        ...state[currentVideoIndex],
        duration,
      }),
    );
  };

  const onProgress = ({
    playedSeconds,
    played,
    loaded,
  }: {
    playedSeconds: number;
    played: number;
    loaded: number;
  }): void => {
    // According to react player docs, isBuffer only works on Files, YouTube and Facebook and so, it is not reliable on URLs.
    // So, this is a workaround to show the buffering state in case after several calls to onProgress the previous played seconds
    // are the same as the current played seconds. If that happens and playing is true, player is on buffering state.
    if (
      videoState[currentVideoIndex].playing &&
      videoState[currentVideoIndex].playedSeconds ===
        playerRef.current?.getCurrentTime()
    ) {
      setIsBuffering(true);
    } else if (isBuffering) {
      setIsBuffering(false);
    }
    setVideoState((state) =>
      updateVideoStateArray(state, currentVideoIndex, {
        ...state[currentVideoIndex],
        played,
        loaded,
        playedSeconds,
      }),
    );
  };

  const updatePlayed = (value: number) => {
    setVideoState((s) =>
      updateVideoStateArray(s, currentVideoIndex, {
        ...s[currentVideoIndex],
        played: value,
      }),
    );
  };

  const seekTo = (value: number) => {
    playerRef.current?.seekTo(value);
  };

  const onStart = () => {
    // Need to add a 300ms delay for mobile Safari since the video player resizes causing a flash effect.
    setTimeout(
      () =>
        setVideoState((state) =>
          updateVideoStateArray(state, currentVideoIndex, {
            ...state[currentVideoIndex],
            hasStarted: true,
          }),
        ),
      isIos && (isMobile || isWebview) ? 300 : 0,
    );
  };

  const onError = (error: any) => {
    logBugsnag(SEVERITY.error, error?.message);

    setVideoState((s) =>
      updateVideoStateArray(s, currentVideoIndex, {
        ...s[currentVideoIndex],
        muted: true,
      }),
    );
    // The following line is required to autoplay videos after muting them in Android
    if (isAndroid && !hasTriedPlayingMuted) {
      setHasTriedPlayingMuted(true);
      setTimeout(() => playerRef.current?.getInternalPlayer()?.play());
    }
  };

  const moveToNextRowItem = useCallback(() => {
    const currentIndex = currentVideoIndexRef.current!;
    const newVideoIndex = currentIndex + 1;
    if (newVideoIndex < videos!.length) {
      sendTelemetry(dispatchClick, videos[newVideoIndex], videos[currentIndex]);
      requestAnimationFrame(() =>
        itemsRef.current[newVideoIndex].scrollIntoView({
          behavior: isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
        }),
      );
      setCurrentVideoIndex(newVideoIndex);
      setVideoState((state) =>
        updateVideoStateArray(state, newVideoIndex, {
          ...initialVideoState,
          muted: state[currentIndex].muted,
        }),
      );
      setTimeout(() => {
        document.title = buildPageTitle(newVideoIndex, videos);
      }, 200);
    } else {
      itemsRef.current[currentVideoIndexRef.current!].scrollIntoView({
        behavior: isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
      });
    }
  }, [currentVideoIndexRef, setCurrentVideoIndex, videos, dispatchClick]);

  const moveToPreviousRowItem = useCallback(() => {
    const currentIndex = currentVideoIndexRef.current!;
    const newVideoIndex = currentIndex - 1;
    if (newVideoIndex >= 0) {
      sendTelemetry(dispatchClick, videos[newVideoIndex], videos[currentIndex]);
      requestAnimationFrame(() =>
        itemsRef.current[newVideoIndex].scrollIntoView({
          behavior: isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
        }),
      );
      setCurrentVideoIndex(newVideoIndex);
      setVideoState((state) =>
        updateVideoStateArray(state, newVideoIndex, {
          ...initialVideoState,
          muted: state[currentIndex].muted,
        }),
      );
      setTimeout(() => {
        document.title = buildPageTitle(newVideoIndex, videos);
      }, 200);
    } else {
      itemsRef.current[currentVideoIndexRef.current!].scrollIntoView({
        behavior: isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
      });
    }
  }, [currentVideoIndexRef, setCurrentVideoIndex, videos, dispatchClick]);

  const moveToCurrentRowItem = useCallback(() => {
    const currentIndex = currentVideoIndexRef.current!;
    requestAnimationFrame(() =>
      itemsRef.current[currentIndex].scrollIntoView({
        behavior: isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
      }),
    );
  }, [currentVideoIndexRef]);

  useEffect(() => {
    if (!videoCollectionListLoading && !loadingVideo && loading) {
      if (!video) {
        setLoading(false);
        return;
      }

      let currentIndex = videoList.findIndex((v) => v?.sys.id === id_video);
      if (currentIndex < 0) {
        currentIndex = 0;
        setVideos([video]);
        setVideoState([initialVideoState]);
      } else {
        setVideos(videoList);
        setVideoState(videoList.map(() => initialVideoState));
      }

      setCurrentVideoIndex(currentIndex);
      // This chunk of code runs inside the timeout to allow the thumbnail list to render and then be able to scroll.
      setTimeout(() => {
        itemsRef.current[currentIndex].scrollIntoView();
        // This is to prevent to show the wrong thumbnail at the beginning, since the list has to render first,
        // and then it has to scroll. So by deferring setting loading to false by 200ms that issue won't happen.
        setTimeout(() => setLoading(false), 200);
        document.title = buildPageTitle(currentIndex, videoList);
      });
    }
  }, [
    videoList,
    id_video,
    video,
    loading,
    loadingVideo,
    videoCollectionListLoading,
    setCurrentVideoIndex,
  ]);

  useEffect(() => {
    if (!loading) {
      itemsRef.current[currentVideoIndexRef.current!].scrollIntoView();
    }
  }, [currentVideoIndexRef, itemSize, loading]);

  useEffect(() => {
    if (!isMobile && !isTablet) return;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) return;
      touchEnd.current = null;
      touchStart.current = e.targetTouches[0].clientY;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Do not stop propagation if moving video progress slider
      if ((e.target as HTMLInputElement)?.type === 'range') {
        return;
      }
      if (
        Math.abs(touchStart.current! - e.targetTouches[0].clientY) < 40 &&
        !isScrollingRef.current
      ) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      const target = e.target as any;
      if (target && target.closest('#video-controls')) {
        return;
      }
      if (!isScrollingRef.current) {
        isScrollingRef.current = true;
        setIsScrolling(true);
      }
      touchEnd.current = e.targetTouches[0].clientY;
      scrollableRef.current!.scrollTo({
        top:
          itemsRef.current[currentVideoIndexRef.current!].offsetTop +
          (touchStart.current! - touchEnd.current!),
      });
      return false;
    };

    const onTouchEnd = () => {
      if (!touchStart.current || !touchEnd.current) return;
      const distance = touchStart.current! - touchEnd.current!;
      const isUpSwipe = distance > itemSize / 5;
      const isDownSwipe = distance < -itemSize / 5;
      touchStart.current = null;
      if (isUpSwipe) {
        moveToNextRowItem();
      } else if (isDownSwipe) {
        moveToPreviousRowItem();
      } else {
        moveToCurrentRowItem();
      }
      setTimeout(() => (isScrollingRef.current = false), 100);
    };

    const videosListElement = scrollableRef.current;

    if (videosListElement) {
      videosListElement.addEventListener('touchstart', onTouchStart, {
        passive: false,
      });
      videosListElement.addEventListener('touchmove', onTouchMove, {
        passive: false,
      });
      videosListElement.addEventListener('touchend', onTouchEnd, {
        passive: false,
      });
      videosListElement.addEventListener('touchcancel', onTouchEnd, {
        passive: false,
      });

      return () => {
        videosListElement?.removeEventListener('touchstart', onTouchStart);
        videosListElement?.removeEventListener('touchmove', onTouchMove);
        videosListElement?.removeEventListener('touchend', onTouchEnd);
        videosListElement?.removeEventListener('touchcancel', onTouchEnd);
      };
    }
  }, [
    videos,
    loading,
    isMobile,
    isTablet,
    scrollableRef,
    itemSize,
    currentVideoIndexRef,
    moveToCurrentRowItem,
    moveToNextRowItem,
    moveToPreviousRowItem,
  ]);

  useEffect(() => {
    if (!isDesktop && !isTablet) return;
    const onWheel = (e: WheelEvent) => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      e.preventDefault();
      e.stopPropagation();
      if (!isScrollingRef.current && e.deltaY !== 0) {
        isDesktopScrollCompleteRef.current = false;
        isScrollingRef.current = true;
        setIsScrolling(true);
        if (e.deltaY > 0) {
          if (currentVideoIndexRef.current < itemsRef.current.length - 1) {
            moveToNextRowItem();
          } else {
            // Creating the bounce effect for the last video
            bounceEndRef.current?.scrollIntoView({
              behavior:
                isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
            });
            setTimeout(() => moveToCurrentRowItem(), 200);
          }
        } else if (e.deltaY < 0) {
          if (currentVideoIndexRef.current > 0) {
            moveToPreviousRowItem();
          } else {
            // Creating the bounce effect for the first video
            bounceStartRef.current?.scrollIntoView({
              behavior:
                isChrome || (isAndroid && isWebview) ? 'auto' : 'smooth',
            });
            setTimeout(() => moveToCurrentRowItem(), 200);
          }
        }
      }

      // SetTimeout callback will be executed once the momentum of the wheel has stopped
      scrollTimeoutRef.current = setTimeout(() => {
        // It could happen that the momentum of the wheel is small, so it finishes before the
        // scroll animation ends. To overcome this situation, an interval is used to check if
        // scroll has finished.
        let waitCount = 0;
        const wait = setInterval(() => {
          if (isDesktopScrollCompleteRef.current) {
            isScrollingRef.current = false;
            setIsScrolling(false);
            clearInterval(wait);
          }
          if (waitCount > 10) {
            clearInterval(wait);
          }
          waitCount++;
        }, 50);
      }, 50);
      return false;
    };

    const videosListElement = scrollableRef.current;

    if (videosListElement) {
      videosListElement.addEventListener('wheel', onWheel);

      return () => {
        videosListElement?.removeEventListener('wheel', onWheel);
      };
    }
  }, [
    videos,
    itemSize,
    loading,
    isDesktop,
    isTablet,
    scrollableRef,
    currentVideoIndexRef,
    moveToCurrentRowItem,
    moveToNextRowItem,
    moveToPreviousRowItem,
  ]);

  const onScroll = () => {
    if (loading) return;
    if (isScrollTimeoutRef.current) clearTimeout(isScrollTimeoutRef.current);
    isScrollTimeoutRef.current = setTimeout(() => {
      isDesktopScrollCompleteRef.current = true;
      if (isScrollingRef.current === false) {
        setIsScrolling(false);
      }
    }, 200);
  };

  const onSubmitVideo = (videoName: string) => () => {
    if (dispatchVideoPageClick) {
      dispatchVideoPageClick('nhWeb_submitYourVideo', [videoName], {
        itemType: 'button',
      });
    }
    setIsSubmitVideoModalOpen(true);
  };

  const onSubmitVideoClose = () => {
    setIsSubmitVideoModalOpen(false);
  };

  if (loadingVideo) {
    return (
      <Flex alignItems="center" justifyContent="center" height="100%">
        <Loading />
      </Flex>
    );
  }

  if (!video && !loading) {
    return <NotFound />;
  }

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      overflowY="hidden"
      bgColor="rgba(18, 18, 18, 1)"
    >
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, interactive-widget=resizes-content"
        />
      </Helmet>
      <SubmitVideoModal
        isOpen={isSubmitVideoModalOpen}
        onClose={onSubmitVideoClose}
      />
      <Box
        position="relative"
        data-testid="thumbnail-list"
        height="100dvh"
        ref={scrollableRef}
        style={{ overflowY: 'hidden', zIndex: 4 }}
        onScroll={onScroll}
      >
        <Box key="top" ref={bounceStartRef} height="100px" />
        {videoState.length > 0 &&
          videos.map((v, i) =>
            Math.abs(i - currentVideoIndex!) <= 1 ? (
              <VideoThumbnailItem
                key={v.sys.id}
                onSubmitYourVideo={onSubmitVideo(v.name as string)}
                innerRef={(el) => (itemsRef.current[i] = el!)}
                video={v}
                isScrolling={isScrolling}
                isBuffering={isBuffering}
                playing={videoState[i].playing}
                ended={videoState[i].ended}
                muted={videoState[i].muted}
                played={videoState[i].played}
                loaded={videoState[i].loaded}
                duration={videoState[i].duration}
                onPlay={onPlay}
                play={play}
                pause={pause}
                toggleMute={toggleMute}
                updatePlayed={updatePlayed}
                seekTo={seekTo}
                hasStarted={videoState[i].hasStarted || loading}
              />
            ) : (
              <Box
                ref={(el) => (itemsRef.current[i] = el!)}
                height="100dvh"
                key={v.sys.id}
              />
            ),
          )}
        <Box
          key="bottom"
          ref={bounceEndRef}
          height="100px"
          textStyle="body-small-medium"
          color="white"
          display="flex"
          pt="10"
          justifyContent="center"
        >
          {!loading && <span>You’ve reached the bottom</span>}
        </Box>
      </Box>
      {videoState.length > 0 && (
        <ReactPlayer
          playing={videoState[currentVideoIndex].playing}
          muted={videoState[currentVideoIndex].muted}
          ref={playerRef}
          width="100vw"
          height="100dvh"
          style={{
            position: 'absolute',
            top: '0px',
            pointerEvents: 'none',
            opacity: isScrolling ? 0 : 1,
          }}
          url={videos[currentVideoIndex!]?.originalVideo?.url!}
          orientation={videos[currentVideoIndex!]?.orientation!}
          onProgress={onProgress}
          onStart={onStart}
          onDuration={onDuration}
          onEnded={onEnded}
          controls={false}
          progressInterval={1000}
          data-testid="video-player"
          config={{
            file: {
              attributes: {
                crossOrigin: 'anonymous',
                style: {
                  zIndex: 3,
                  objectFit: 'contain',
                  position: 'absolute',
                  top: '0px',
                  left: '0px',
                  height: '100%',
                  width: '100%',
                },
              },
            },
          }}
          playsinline
          volume={1}
          onBuffer={onBuffer}
          onBufferEnd={onBufferEnd}
          onError={onError}
        />
      )}
    </Box>
  );
};
